/**
 * pw-newsletter-signup.js -- the JS behind snippets/pw-signup-form.liquid, which serves EVERY email
 * signup surface: site footer, Saturday Spark, Coming Soon and the home newsletter band.
 *
 * History. 2026-09-21 (tasks.db, Paul-confirmed bot fraud): the home band's Shopify-native
 * `{% form 'customer' %}` posted straight to Shopify's /contact endpoint with no mitigation, so it
 * was replaced by a POST to PageWright's /api/newsletter-signup. 2026-09-28 (signup bot defense
 * P1): the footer, Saturday Spark and Coming Soon forms were native too (the lockdown finding: the
 * ~1,482 bot customers came in through those), so all four now share this file via one snippet.
 *
 *  1. Honeypot (`website`) and the mount-time `elapsedMs` are still sent (the API's legacy gate
 *     reads them while its server-verified guard is off). The server decides; whatever it answers,
 *     a bot sees the same success message a person would.
 *  2. Consent (tasks.db #1290): the API requires `consent` to be the exact JSON boolean `true`. We
 *     send the REAL state of the required, unchecked-by-default `[data-pw-newsletter-consent]`
 *     checkbox -- never a hardcoded `true` -- and refuse to POST while it is unticked, with visible
 *     feedback (the form is `novalidate`, so native constraint validation never fires). If the
 *     checkbox is missing from the markup (a stale cached section) we send `consent: false` and let
 *     the API's `consent_required` message surface -- consent is never fabricated.
 *  3. `surface` (from the form's data-surface) tells the API which signup this is; the API maps it
 *     to the Shopify source + tags. The client never sends a tag.
 *  4. Idempotent binding: several sections include this script tag on one page, so each form is
 *     bound exactly once (a double-bound form would POST twice).
 *
 * 5. Server-issued form token (P2, 2026-09-28): on mount the form GETs /api/signup-token?ep=newsletter
 *    and sends the token back as `formToken`. The SERVER stamps the issue time, so "how long did the
 *    visitor take" is a server fact (the client `elapsedMs` above was a claim any bot could lie
 *    about). A token endpoint that is down, 404s (API not deployed yet) or answers `token: null`
 *    (guard off) NEVER blocks a signup: the form posts without a token and the server decides.
 * 6. Typo hint (P2): a likely domain typo (gmail.org, gmail.con, yaho.com, comcast.nef) shows a
 *    "Did you mean ...?" suggestion on the first submit. Submitting the SAME address again posts it
 *    as typed: a hint is a suggestion, never a block. The list mirrors the API's `suggestEmailFix`.
 *
 * 7. Cloudflare Turnstile (P3, 2026-09-28): only when the theme setting `turnstile_site_key` is set
 *    (blank = off). The widget renders lazily, once per form, `interaction-only` (invisible unless
 *    Cloudflare needs a challenge). Its token is sent as `turnstileToken` and the widget is reset
 *    after each attempt (tokens are single-use). Submitting before the token exists waits for it
 *    (no lost signup). A widget or script failure fails OPEN client-side: the form still submits
 *    without a token and the SERVER decides. The secret lives only in the API env.
 *
 * 8. Double opt-in (P4): when the API answers `confirm_sent` (NEWSLETTER_DOUBLE_OPT_IN on) the
 *    visitor is NOT subscribed yet; the message tells them to check their email. That is a success
 *    state (not error-styled), the form resets, and the conversion event is the pending kind.
 *
 * No framework, no build step -- same plain-IIFE shape as pw-giveaway-entry.js/pw-freebie-claim.js.
 */
(function () {
  // ── Typo hint (mirrors packages/api/src/signup-guard.ts suggestEmailFix) ───────────────────────
  var TYPO_DOMAINS = {
    'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gmaill.com': 'gmail.com', 'gnail.com': 'gmail.com',
    'gmail.con': 'gmail.com', 'gmail.cm': 'gmail.com', 'gmail.comm': 'gmail.com', 'gmail.co': 'gmail.com', 'gmail.org': 'gmail.com',
    'gmail.net': 'gmail.com', 'gmail.cim': 'gmail.com', 'gmail.vom': 'gmail.com',
    'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'yahoo.con': 'yahoo.com', 'yhoo.com': 'yahoo.com', 'yaoo.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmail.con': 'hotmail.com', 'hotnail.com': 'hotmail.com',
    'outlok.com': 'outlook.com', 'outlook.con': 'outlook.com',
    'icloud.con': 'icloud.com', 'iclould.com': 'icloud.com', 'iclod.com': 'icloud.com',
    'aol.con': 'aol.com', 'comcast.nef': 'comcast.net'
  };
  // A mistyped top-level domain on ANY domain: .con .cmo .ocm .vom .coom .comm -> .com, .nef .ner -> .net
  var TLD_TYPOS = { con: 'com', cmo: 'com', ocm: 'com', vom: 'com', coom: 'com', comm: 'com', nef: 'net', ner: 'net' };
  var TYPO_BASES = [['gmail', 'com'], ['yahoo', 'com'], ['hotmail', 'com'], ['outlook', 'com'], ['icloud', 'com'], ['comcast', 'net']];
  var KNOWN_GOOD = { 'gmail.com': 1, 'yahoo.com': 1, 'aol.com': 1, 'hotmail.com': 1, 'outlook.com': 1, 'icloud.com': 1, 'me.com': 1, 'msn.com': 1, 'live.com': 1, 'comcast.net': 1, 'sbcglobal.net': 1, 'att.net': 1, 'verizon.net': 1, 'cox.net': 1,
    'ymail.com': 1, 'rocketmail.com': 1, 'mac.com': 1, 'charter.net': 1, 'earthlink.net': 1, 'bellsouth.net': 1, 'optonline.net': 1, 'proton.me': 1, 'protonmail.com': 1, 'hey.com': 1,
    'email.com': 1, 'mail.com': 1, 'gmx.com': 1, 'gmx.net': 1, 'googlemail.com': 1, 'fastmail.com': 1, 'zoho.com': 1, 'duck.com': 1, 'pm.me': 1, 'aim.com': 1,
    'yahoo.co.uk': 1, 'yahoo.ca': 1, 'hotmail.co.uk': 1, 'live.co.uk': 1, 'btinternet.com': 1, 'shaw.ca': 1, 'rogers.com': 1 };

  function withinOneEdit(a, b) {
    if (a === b) return true;
    if (Math.abs(a.length - b.length) > 1) return false;
    var i;
    if (a.length === b.length) {
      var diffs = [];
      for (i = 0; i < a.length; i++) if (a.charAt(i) !== b.charAt(i)) diffs.push(i);
      if (diffs.length === 1) return true;
      return diffs.length === 2 && diffs[1] === diffs[0] + 1 && a.charAt(diffs[0]) === b.charAt(diffs[1]) && a.charAt(diffs[1]) === b.charAt(diffs[0]);
    }
    var s = a.length < b.length ? a : b;
    var l = a.length < b.length ? b : a;
    i = 0;
    while (i < s.length && s.charAt(i) === l.charAt(i)) i++;
    return s.slice(i) === l.slice(i + 1);
  }

  function suggestFix(email) {
    var at = email.lastIndexOf('@');
    if (at < 1) return '';
    var local = email.slice(0, at);
    var domain = email.slice(at + 1).toLowerCase();
    if (!domain || KNOWN_GOOD[domain]) return '';
    if (TYPO_DOMAINS[domain]) return local + '@' + TYPO_DOMAINS[domain];
    var dot = domain.lastIndexOf('.');
    if (dot > 0) {
      var sld = domain.slice(0, dot);
      var tld = domain.slice(dot + 1);
      if (TLD_TYPOS[tld]) return local + '@' + sld + '.' + TLD_TYPOS[tld];
      for (var k = 0; k < TYPO_BASES.length; k++) {
        var base = TYPO_BASES[k][0];
        if (tld === TYPO_BASES[k][1] && sld.length >= 5 && sld !== base && withinOneEdit(sld, base)) return local + '@' + base + '.' + tld;
      }
    }
    return '';
  }

  // ── Cloudflare Turnstile loader (kept in sync with pw-freebie-claim.js) ───────────────────────
  var TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var turnstileWaiters = null;
  function ensureTurnstile(cb) {
    if (window.turnstile) return cb(true);
    if (turnstileWaiters) return turnstileWaiters.push(cb);
    turnstileWaiters = [cb];
    var done = function (ok) {
      var w = turnstileWaiters;
      turnstileWaiters = null;
      w.forEach(function (fn) {
        fn(ok);
      });
    };
    try {
      var el = document.createElement('script');
      el.src = TURNSTILE_SRC;
      el.async = true;
      el.defer = true;
      el.onload = function () {
        done(!!window.turnstile);
      };
      el.onerror = function () {
        done(false);
      };
      document.head.appendChild(el);
    } catch (e) {
      done(false);
    }
  }

  document.querySelectorAll('[data-pw-newsletter-form]').forEach(function (form) {
    if (form.__pwSignupBound) return;
    form.__pwSignupBound = true;

    var section = form.closest('[data-backend-url]');
    if (!section) return;

    var backendUrl = section.getAttribute('data-backend-url') || (window && window.PW_BACKEND_URL) || '';
    var successMessage = section.getAttribute('data-success-message') || 'Thanks — see you Saturday.';
    var surface = form.getAttribute('data-surface') || (section.getAttribute && section.getAttribute('data-surface')) || '';
    var mountedAt = Date.now();

    var emailInput = form.querySelector('[data-pw-newsletter-email]');
    var hpInput = form.querySelector('[data-pw-newsletter-hp]');
    var submitBtn = form.querySelector('[data-pw-newsletter-submit]');
    var consentInput = form.querySelector('[data-pw-newsletter-consent]');
    var statusEl = section.querySelector('[data-pw-newsletter-status]');
    var suggestBtn = form.querySelector('[data-pw-newsletter-suggest]');
    var guardEndpoint = form.getAttribute('data-guard-endpoint') || 'newsletter';
    var hintShownFor = '';
    var RETRY_DELAY_MS = 2600;
    var siteKey = section.getAttribute('data-turnstile-sitekey') || '';
    var tsBox = form.querySelector('[data-pw-turnstile]');
    var tsWidget = null;
    var tsToken = '';
    var tsFailed = false;
    var tsPending = false;

    function say(message, isError) {
      if (!statusEl) return;
      statusEl.textContent = message;
      statusEl.hidden = false;
      if (isError) statusEl.setAttribute('data-error', '');
      else statusEl.removeAttribute('data-error');
    }

    // ── Server-issued form token (P2). Never blocks: any failure -> null -> post without one. ───
    function fetchToken() {
      try {
        return fetch(backendUrl + '/api/signup-token?ep=' + guardEndpoint, { method: 'GET', credentials: 'omit' })
          .then(function (res) {
            return res && res.ok ? res.json() : null;
          })
          .then(function (body) {
            // The server also sends `minAgeMs` / `ttlSec` (signup-token.ts); the constants below are only
            // the fallback if an older API omits them. `issuedAt` is the CLIENT clock at receipt.
            return body && body.token ? { token: body.token, minAgeMs: body.minAgeMs, ttlSec: body.ttlSec, issuedAt: Date.now() } : null;
          })
          .catch(function () {
            return null;
          });
      } catch (e) {
        return Promise.resolve(null);
      }
    }
    var tokenPromise = fetchToken();

    // tasks.db #2381 / #2114: the guard is in ENFORCE and a token lives 30 min (server FORM_TOKEN_TTL_SEC),
    // so a page left open (or whose mount fetch failed) must not refuse a real person. Submit swaps a
    // stale/missing token for a fresh one and waits out the new token's minimum age; if the server STILL
    // says token_expired/token_invalid it retries ONCE transparently. Same pattern as pw-freebie-claim.js.
    var TOKEN_TTL_FALLBACK_S = 1800;
    var TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000; // refresh this long before the TTL
    var TOKEN_MIN_AGE_FALLBACK_MS = 2500; // coupled to FORM_TOKEN_MIN_AGE_MS in pagewright's signup-guard.ts
    var TOKEN_MIN_AGE_PAD_MS = 500;
    var TOKEN_MIN_AGE_MAX_MS = 10000;
    var RELOAD_MESSAGE = 'This page has been open a while. Please reload it and try again.';
    var CONNECTION_MESSAGE = 'We couldn’t reach the server. Please check your connection and try again.';
    // Coerce first, then test: a legitimate server 0 is a value, not 'missing'; null/''/NaN/negative fall back.
    function serverNumber(v, fallback) {
      if (v === null || v === undefined || v === '') return fallback;
      var n = Number(v);
      return typeof n === 'number' && isFinite(n) && n >= 0 ? n : fallback;
    }
    // PII-free health/analytics signal (Rule 13): outcome is 'recovered' | 'failed'. Through the gate.
    function trackRefresh(outcome) {
      try {
        if (window.PWAnalyticsGate) window.PWAnalyticsGate.gtagEvent('newsletter_token_refresh', { outcome: outcome });
      } catch (e) {
        /* analytics never blocks a signup */
      }
    }
    // Resolves to { token, ok }. `force` = the server just refused our token: a failed refetch is then
    // NOT papered over with the dead token (ok:false). Otherwise a failed refetch falls back to what we
    // had (never block; the server decides).
    function tokenForSubmit(force) {
      return tokenPromise
        .then(function (info) {
          var ttlMs = serverNumber(info && info.ttlSec, TOKEN_TTL_FALLBACK_S) * 1000;
          if (!force && info && Date.now() - info.issuedAt <= ttlMs - TOKEN_REFRESH_MARGIN_MS) return { info: info, fresh: false };
          tokenPromise = fetchToken();
          return tokenPromise.then(function (fresh) {
            if (fresh) return { info: fresh, fresh: true };
            return force ? { info: null, fresh: false, failed: true } : { info: info, fresh: false };
          });
        })
        .then(function (r) {
          if (r.failed) return { ok: false };
          if (!r.info) return { ok: true, token: undefined };
          if (!r.fresh) return { ok: true, token: r.info.token };
          var minAge = Math.min(serverNumber(r.info.minAgeMs, TOKEN_MIN_AGE_FALLBACK_MS), TOKEN_MIN_AGE_MAX_MS) + TOKEN_MIN_AGE_PAD_MS;
          var wait = Math.max(0, minAge - (Date.now() - r.info.issuedAt));
          if (wait === 0) return { ok: true, token: r.info.token };
          say('One moment…', false);
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve({ ok: true, token: r.info.token });
            }, wait);
          });
        });
    }

    // ── Turnstile (P3): render once, interaction-only. Off unless a site key is configured. ─────
    if (siteKey && tsBox) {
      ensureTurnstile(function (ok) {
        if (!ok) {
          tsFailed = true;
          if (tsPending) runPending();
          return;
        }
        if (tsWidget !== null) return;
        tsWidget = window.turnstile.render(tsBox, {
          sitekey: siteKey,
          appearance: 'interaction-only',
          callback: function (t) {
            tsToken = t;
            if (tsPending) runPending();
          },
          'error-callback': function () {
            tsFailed = true;
            if (tsPending) runPending();
          },
          'expired-callback': function () {
            tsToken = '';
          },
        });
      });
    }
    function resetTurnstile() {
      tsToken = '';
      if (siteKey && tsWidget !== null && window.turnstile && window.turnstile.reset) window.turnstile.reset(tsWidget);
    }
    function runPending() {
      tsPending = false;
      submitFlow();
    }

    // ── Typo hint ───────────────────────────────────────────────────────────────────────────────
    function hideHint() {
      if (suggestBtn) suggestBtn.hidden = true;
    }
    function showHint(suggestion, typed) {
      hintShownFor = typed;
      say('Did you mean ' + suggestion + '?', false);
      if (suggestBtn) {
        suggestBtn.textContent = 'Use ' + suggestion;
        suggestBtn.hidden = false;
      }
    }
    if (suggestBtn) {
      suggestBtn.addEventListener('click', function (evt) {
        if (evt && evt.preventDefault) evt.preventDefault();
        var fix = suggestFix(emailInput ? emailInput.value.trim() : '');
        if (fix && emailInput) emailInput.value = fix;
        else if (suggestBtn.getAttribute && suggestBtn.textContent) emailInput.value = suggestBtn.textContent.replace(/^Use\s+/, '');
        hintShownFor = '';
        hideHint();
        if (statusEl) statusEl.hidden = true;
      });
    }

    if (consentInput) {
      consentInput.addEventListener('change', function () {
        // Clear the consent nudge as soon as the box is ticked -- no stale error.
        if (consentInput.checked && statusEl) statusEl.hidden = true;
      });
    }

    // Tokens are single-use and the server may have consumed this one: ALWAYS fetch a fresh token after
    // an attempt (review fix), so a retry after a 5xx / 429 is never a spent-token replay.
    function finish() {
      submitBtn.disabled = false;
      tokenPromise = fetchToken();
    }

    function post(email, retried, refreshed) {
      return tokenForSubmit(!!refreshed).then(function (t) {
        if (!t.ok) {
          // The server refused our token and a fresh one could not be fetched: do not re-POST the dead one.
          trackRefresh('failed');
          say(CONNECTION_MESSAGE, true);
          finish();
          return;
        }
        var token = t.token;
        var payload = {
          email: email,
          website: hpInput ? hpInput.value : '',
          elapsedMs: Date.now() - mountedAt,
          consent: !!(consentInput && consentInput.checked),
        };
        if (surface) payload.surface = surface;
        if (token) payload.formToken = token;
        if (tsToken) payload.turnstileToken = tsToken;

        return fetch(backendUrl + '/api/newsletter-signup', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        })
          .then(function (res) {
            return res
              .json()
              .catch(function () {
                return {};
              })
              .then(function (body) {
                return { ok: res.ok, status: res.status, body: body };
              });
          })
          .then(function (result) {
            var code = result.body && result.body.code;
            var message = (result.body && result.body.message) || '';

            if (code === 'subscribed') {
              if (refreshed) trackRefresh('recovered');
              say(successMessage, false);
              hideHint();
              form.reset();
              tokenPromise = fetchToken(); // tokens are single-use
              // #1722: through the gate -- withheld on staging/preview themes (assets/pw-analytics-gate.js).
              if (window.PWAnalyticsGate) window.PWAnalyticsGate.gtagEvent('newsletter_signup');
              return;
            }

            if (code === 'confirm_sent') {
              if (refreshed) trackRefresh('recovered');
              say(message || 'Almost there — check your email and tap the link to confirm.', false);
              hideHint();
              form.reset();
              tokenPromise = fetchToken();
              // #1722: through the gate -- withheld on staging/preview themes (assets/pw-analytics-gate.js).
              if (window.PWAnalyticsGate) window.PWAnalyticsGate.gtagEvent('newsletter_signup_pending');
              return;
            }

            if (code === 'token_expired' || code === 'token_invalid') {
              if (!refreshed) {
                // Page left open past the token TTL: fetch a fresh token and retry ONCE, transparently.
                say('One moment…', false);
                return post(email, retried, true);
              }
              trackRefresh('failed');
              say(RELOAD_MESSAGE, true);
              finish();
              return;
            }

            if (code === 'try_again' && !retried) {
              // Submitted inside the server's minimum age (autofill + Enter, a very fast typist):
              // wait it out and retry ONCE with the same token. Not consumed on refusal.
              say('One moment…', false);
              return new Promise(function (resolve) {
                setTimeout(resolve, RETRY_DELAY_MS);
              }).then(function () {
                return post(email, true, refreshed);
              });
            }

            if (code === 'invalid_email' && result.body && result.body.suggestion) {
              showHint(result.body.suggestion, email);
              finish();
              return;
            }

            if (result.status === 429) {
              say(message || 'Too many signups from here — please try again in a bit.', true);
              finish();
              return;
            }

            // token_invalid / try_again (twice) / email_not_accepted / turnstile_fail / anything else.
            say(message || 'Something went wrong — please try again in a moment.', true);
            finish();
          });
      });
    }

    function submitFlow() {
      // A submit already in flight (button disabled) -- double Enter / scripted requestSubmit -- is ignored.
      if (submitBtn.disabled) return;
      var email = emailInput ? emailInput.value.trim() : '';
      if (!email) {
        say('Please enter your email address.', true);
        return;
      }

      // Consent gate: never POST with the box unticked (button click, Enter key, scripted submit).
      if (consentInput && !consentInput.checked) {
        say('Please tick the box to confirm you’d like our emails.', true);
        consentInput.focus();
        return;
      }

      // Typo hint: show once per address; submitting the SAME address again posts it as typed.
      var fix = suggestFix(email);
      if (fix && hintShownFor !== email) {
        showHint(fix, email);
        return;
      }

      // Turnstile: wait for the token if the widget is still working (no lost signup). A widget or
      // script failure (tsFailed) fails OPEN: post without a token and let the server decide.
      if (siteKey && tsBox && !tsToken && !tsFailed) {
        tsPending = true;
        say('One moment — verifying you’re human…', false);
        return;
      }

      submitBtn.disabled = true;
      hideHint();
      say('Joining…', false);

      post(email, false)
        .then(resetTurnstile)
        .catch(function () {
          say('Something went wrong — please try again in a moment.', true);
          resetTurnstile();
          finish();
        });
    }

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      submitFlow();
    });
  });
})();
